import { computed, onBeforeUnmount, ref } from "vue";

function isHoverEffectEnabled(config) {
  return !!config?.hoverEffect?.enabled && config.hoverEffect.effects?.length > 0;
}

function hasEffect(config, effectName) {
  return config?.hoverEffect?.effects?.includes(effectName);
}

export function useHoverEffect(configSource) {
  const hovering = ref(false);
  const transitioning = ref(false);
  let cleanupTimer = null;

  const currentConfig = computed(() => {
    return typeof configSource === "function" ? configSource() || {} : configSource || {};
  });

  const hoverEffect = computed(() => currentConfig.value?.hoverEffect || {});

  const resetCleanupTimer = () => {
    if (cleanupTimer) {
      clearTimeout(cleanupTimer);
      cleanupTimer = null;
    }
  };

  const getTransitionStyle = () => {
    const duration = hoverEffect.value.duration || 300;
    const easing = hoverEffect.value.easing || "ease-out";
    return {
      transition: [
        `background-color ${duration}ms ${easing}`,
        `opacity ${duration}ms ${easing}`,
        `transform ${duration}ms ${easing}`,
        `box-shadow ${duration}ms ${easing}`,
      ].join(", "),
    };
  };

  const onMouseenter = () => {
    if (!isHoverEffectEnabled(currentConfig.value)) {
      return;
    }
    resetCleanupTimer();
    hovering.value = true;
    transitioning.value = true;
  };

  const onMouseleave = () => {
    if (!transitioning.value && !hovering.value) {
      return;
    }
    resetCleanupTimer();
    hovering.value = false;
    transitioning.value = true;
    const duration = hoverEffect.value.duration || 300;
    cleanupTimer = setTimeout(() => {
      transitioning.value = false;
      cleanupTimer = null;
    }, duration + 80);
  };

  const wrapperHoverStyle = computed(() => {
    if (!isHoverEffectEnabled(currentConfig.value)) {
      return {};
    }
    if (!hovering.value && !transitioning.value) {
      return {};
    }
    const style = getTransitionStyle();
    if (hovering.value && hasEffect(currentConfig.value, "opacity")) {
      style.opacity = hoverEffect.value.opacity ?? 1;
    }
    if (hovering.value && hasEffect(currentConfig.value, "scale")) {
      style.transform = `scale(${hoverEffect.value.scale || 1.04})`;
    }
    if (hovering.value) {
      style.willChange = "opacity, transform";
    }
    return style;
  });

  const contentHoverStyle = computed(() => {
    if (!isHoverEffectEnabled(currentConfig.value)) {
      return {};
    }
    if (!hovering.value && !transitioning.value) {
      return {};
    }
    const style = getTransitionStyle();
    if (hovering.value && hasEffect(currentConfig.value, "background")) {
      style.backgroundColor =
        hoverEffect.value.backgroundColor || "rgba(255, 255, 255, 0)";
    }
    if (hovering.value && hasEffect(currentConfig.value, "shadow")) {
      style.boxShadow =
        hoverEffect.value.shadow || "0 8px 24px rgba(0, 0, 0, 0.16)";
    }
    return style;
  });

  const hoverEffectEvents = {
    mouseenter: onMouseenter,
    mouseleave: onMouseleave,
  };

  onBeforeUnmount(() => {
    resetCleanupTimer();
  });

  return {
    hoverEffectEvents,
    wrapperHoverStyle,
    contentHoverStyle,
  };
}
